import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { PRODUCT_QUANTITY_CONSTRAINTS } from "../pos"
import { Product, ProductSchema } from "../pos"
import { BikeFrameSize } from "../service/bike-rider-height-utils"
import { PartGrade } from "../service/part-grade"
import { Receipt } from "../service/receipt"
import {
  ServiceBikeBrakeSystem,
  ServiceBikeGearSystem,
  ServiceBikeType,
} from "../service/service-bike"
import { ServiceAction } from "../service/service-part-action"
import { ProposalBikeCondition } from "../service/service-proposal-context"
import { StorageLocationDto } from "../storage-location/storage-location-dto"
import { BikeForSaleStatus } from "./bike-for-sale-status"
import { BikeForSaleType } from "./bike-for-sale-type"
import { SelfBillingInvoiceDto } from "./self-billing-invoice-dto"

export enum BikeForSaleImageSlot {
  FULL_BIKE_RIGHT = "FULL_BIKE_RIGHT",
  FULL_BIKE_LEFT = "FULL_BIKE_LEFT",
  CRANKSET = "CRANKSET",
  REAR_GEAR = "REAR_GEAR",
  SADDLE = "SADDLE",
  HANDLEBAR = "HANDLEBAR",
  FRONT_BRAKE = "FRONT_BRAKE",
  REAR_WHEEL = "REAR_WHEEL",
  DEFECT = "DEFECT",
}

export enum BikeForSalePublicationTarget {
  BIKE_PATH_SHOPIFY = "BIKE_PATH_SHOPIFY",
}

/** Sales channel — mirror of the Prisma enum with the same name. */
export enum BikeSoldVia {
  JULO_SE = "JULO_SE",
  MANUAL = "MANUAL",
}

export const BIKE_FOR_SALE_PUBLICATION_TARGET_LABELS: Record<
  BikeForSalePublicationTarget,
  string
> = {
  [BikeForSalePublicationTarget.BIKE_PATH_SHOPIFY]: "BikePath (Shopify)",
}

export type BikeForSaleStandardPhotoSlot = Exclude<
  BikeForSaleImageSlot,
  BikeForSaleImageSlot.DEFECT
>

export const BIKE_FOR_SALE_STANDARD_PHOTO_SLOTS: ReadonlyArray<BikeForSaleStandardPhotoSlot> =
  [
    BikeForSaleImageSlot.FULL_BIKE_RIGHT,
    BikeForSaleImageSlot.CRANKSET,
    BikeForSaleImageSlot.REAR_GEAR,
    BikeForSaleImageSlot.SADDLE,
    BikeForSaleImageSlot.HANDLEBAR,
    BikeForSaleImageSlot.FRONT_BRAKE,
    BikeForSaleImageSlot.REAR_WHEEL,
    BikeForSaleImageSlot.FULL_BIKE_LEFT,
  ]

export const BIKE_FOR_SALE_MAX_DEFECT_PHOTOS = 5

// Money/measurement values travel as strings to survive JSON without float
// precision loss (they end up in Prisma Decimal columns). Reject anything
// that is not a plain non-negative decimal number — a stray "abc" would
// otherwise surface as NaN in margin math or a 500 from the database.
// Bounded to max 8 integer digits and 2 decimals: prices and weights never
// legitimately exceed that, and it keeps nonsense magnitudes out of the DB.
const NUMBER_AS_STRING = z
  .union([z.string(), z.number()])
  .transform((value) => (typeof value === "string" ? value : value.toString()))
  .refine((value) => /^\d{1,8}(\.\d{1,2})?$/.test(value.trim()), {
    message: "common.validation.decimalNumber" satisfies TranslationKey,
  })
  .transform((value) => value.trim())

export const BikeForSaleConfigSchema = z.object({
  brakeSystemFront: z.nativeEnum(ServiceBikeBrakeSystem).nullable().optional(),
  brakeSystemBack: z.nativeEnum(ServiceBikeBrakeSystem).nullable().optional(),
  gearSystem: z.nativeEnum(ServiceBikeGearSystem).nullable().optional(),
  bikeHasFrontGears: z.boolean().default(false),
  hasElectricFrontGears: z.boolean().default(false),
  hasElectricRearGears: z.boolean().default(false),
  bikeHasDamperFront: z.boolean().default(false),
  bikeHasDamperBack: z.boolean().default(false),
})

export const CreateBikeForSaleSchema = z.object({
  type: z.nativeEnum(BikeForSaleType).default(BikeForSaleType.SECOND_HAND),
  brand: z.string().min(1),
  model: z.string().optional().nullable(),
  color: z.string().optional().nullable(),
  bikeType: z.nativeEnum(ServiceBikeType),
  isElectric: z.boolean(),
  acquisitionCost: NUMBER_AS_STRING.nullable().optional(),
  estimatedSalePrice: NUMBER_AS_STRING.nullable().optional(),
})

export const UpdateBikeForSaleSchema = z.object({
  brand: z.string().min(1).optional(),
  model: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  acquisitionCost: NUMBER_AS_STRING.nullable().optional(),
  estimatedSalePrice: NUMBER_AS_STRING.nullable().optional(),
})

export const UpdateBikeForSaleStatusSchema = z.object({
  status: z.enum([
    BikeForSaleStatus.CREATED,
    BikeForSaleStatus.TO_BE_SERVICED,
    BikeForSaleStatus.WILL_NOT_BE_SERVICED,
  ]),
})

export const SetJuloPublicationSchema = z.object({
  publishedOnJulo: z.boolean(),
})

// Move a bike for sale to a connected store. The optional storage location is
// the bike's new lagerplats at the target store; null clears it so the
// receiving store can assign one later.
export const MoveBikeForSaleSchema = z.object({
  targetStoreId: z.guid(),
  storageLocationId: z.guid().nullable(),
})

export interface BikeForSaleMoveTargetStorageLocationDto {
  id: string
  name: string
}

export interface BikeForSaleMoveTargetDto {
  id: string
  name: string
  city: string
  // The lagerplatser available at the target store (its own + connected
  // peers', same set as its listing form), for the optional picker in the
  // move dialog. Delivered here rather than via the storage-location
  // endpoints because the mover is only authorized at the source store.
  storageLocations: BikeForSaleMoveTargetStorageLocationDto[]
  // Auto-selected in the move dialog; always present in storageLocations.
  defaultStorageLocationId: string | null
}

// Handover of a julo.se-sold bike: the buyer states the six-digit pickup code
// from their confirmation email; skipCodeCheck is the mechanic's override for
// buyers who lost it.
export const MarkBikeForSaleHandedOverSchema = z.object({
  pickupCode: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
  skipCodeCheck: z.boolean().optional(),
})

export const SubmitBikeForSaleInspectionEvaluatedPartSchema = z.object({
  partId: z.guid(),
  grade: z.number().int().min(0).max(10),
  recommendedAction: z.nativeEnum(ServiceAction),
  comment: z.string().nullable().optional(),
  damperBrandId: z.guid().nullable().optional(),
  sortPriority: z.number().int().default(0),
  products: z.array(ProductSchema).default([]),
})

export const SubmitBikeForSaleInspectionManualActionSchema = z.object({
  name: z.string().min(1),
  laborPrice: z.number().int().min(0),
  laborMinutes: z.number().int().min(0).default(0),
})

export const SubmitBikeForSaleInspectionSchema = z.object({
  mechanicId: z.guid(),
  mechanicChose: z.enum([
    BikeForSaleStatus.TO_BE_SERVICED,
    BikeForSaleStatus.WILL_NOT_BE_SERVICED,
  ]),
  estimatedSalePrice: NUMBER_AS_STRING,
  bikeConfig: BikeForSaleConfigSchema,
  evaluatedParts: z
    .array(SubmitBikeForSaleInspectionEvaluatedPartSchema)
    // The DB enforces one row per (inspection, part) — reject duplicates with
    // a 400 instead of letting the unique index 500.
    .refine(
      (parts) =>
        new Set(parts.map((part) => part.partId)).size === parts.length,
      {
        message:
          "common.validation.duplicateInspectionPart" satisfies TranslationKey,
      },
    ),
  removedPartIds: z.array(z.guid()).default([]),
  selectedPackageIds: z.array(z.guid()),
  selectedWashIds: z.array(z.guid()),
  selectedFeeIds: z.array(z.guid()),
  manualActions: z.array(SubmitBikeForSaleInspectionManualActionSchema),
})

export const UpdateBikeForSaleInspectionProductSchema = z.object({
  ean: z.string().nullable().optional(),
  externalId: z.string().nullable().optional(),
  articleNumber: z.string().nullable().optional(),
  acquisitionCost: z
    .number()
    .min(
      0,
      "common.validation.acquisitionCostAtLeastZero" satisfies TranslationKey,
    )
    .nullable()
    .optional(),
  title: z
    .string()
    .min(1, "common.validation.productNameRequired" satisfies TranslationKey)
    .optional(),
  price: z
    .number()
    .min(0, "common.validation.priceAtLeastZero" satisfies TranslationKey)
    .optional(),
  quantity: z
    .number()
    .int()
    .min(PRODUCT_QUANTITY_CONSTRAINTS.min)
    .max(PRODUCT_QUANTITY_CONSTRAINTS.max)
    .optional(),
})

export type UpdateBikeForSaleInspectionProduct = z.infer<
  typeof UpdateBikeForSaleInspectionProductSchema
>

export const SubmitBikeForSaleListingDefectPhotoSchema = z
  .object({
    fileKey: z.string().min(1).optional(),
    existingUrl: z.string().min(1).optional(),
    comment: z.string().max(500).nullable().optional(),
  })
  .refine(
    (photo) => photo.fileKey !== undefined || photo.existingUrl !== undefined,
    { message: "Either fileKey or existingUrl must be provided" },
  )

export const SubmitBikeForSaleListingSchema = z.object({
  // Frame size is captured as a letter size (XS–XL), a centimetre size, or
  // both depending on the bike type. The per-type requirement is enforced on
  // the backend where the bike's type is known, so both fields are optional here.
  frameSizeCm: z.number().int().min(0).nullable().optional(),
  frameSizeLetter: z.nativeEnum(BikeFrameSize).nullable().optional(),
  wheelSizeInches: z.number().int().min(0).nullable().optional(),
  weightKg: NUMBER_AS_STRING.nullable().optional(),
  frameNumber: z
    .string()
    .min(1, "common.validation.required" satisfies TranslationKey)
    .max(255),
  articleNumber: z
    .string()
    .min(1, "common.validation.required" satisfies TranslationKey)
    .max(255),
  numberOfGears: z.number().int().min(0).nullable().optional(),
  listingPrice: NUMBER_AS_STRING,
  storageLocationId: z.guid(),
  standardPhotos: z.partialRecord(
    z.enum([
      BikeForSaleImageSlot.FULL_BIKE_RIGHT,
      BikeForSaleImageSlot.FULL_BIKE_LEFT,
      BikeForSaleImageSlot.CRANKSET,
      BikeForSaleImageSlot.REAR_GEAR,
      BikeForSaleImageSlot.SADDLE,
      BikeForSaleImageSlot.HANDLEBAR,
      BikeForSaleImageSlot.FRONT_BRAKE,
      BikeForSaleImageSlot.REAR_WHEEL,
    ]),
    z.string().min(1),
  ),
  defectPhotos: z
    .array(SubmitBikeForSaleListingDefectPhotoSchema)
    .max(BIKE_FOR_SALE_MAX_DEFECT_PHOTOS)
    .default([]),
})

export type SubmitBikeForSaleListing = z.infer<
  typeof SubmitBikeForSaleListingSchema
>
export type SubmitBikeForSaleListingDefectPhoto = z.infer<
  typeof SubmitBikeForSaleListingDefectPhotoSchema
>

export interface BikeForSaleImageDto {
  id: string
  slot: BikeForSaleImageSlot
  url: string
  comment: string | null
  sortOrder: number
}

export const BikeForSaleProposalPreviewSchema = z.object({
  bikeConfig: BikeForSaleConfigSchema,
  evaluatedParts: z.array(SubmitBikeForSaleInspectionEvaluatedPartSchema),
  removedPartIds: z.array(z.guid()).default([]),
  selectedPackageIds: z.array(z.guid()),
  selectedWashIds: z.array(z.guid()),
  selectedFeeIds: z.array(z.guid()),
  manualActions: z.array(SubmitBikeForSaleInspectionManualActionSchema),
  estimatedSalePrice: NUMBER_AS_STRING,
})

export type BikeForSaleConfig = z.infer<typeof BikeForSaleConfigSchema>
export type CreateBikeForSale = z.infer<typeof CreateBikeForSaleSchema>
export type UpdateBikeForSale = z.infer<typeof UpdateBikeForSaleSchema>
export type UpdateBikeForSaleStatus = z.infer<
  typeof UpdateBikeForSaleStatusSchema
>
export type SetJuloPublication = z.infer<typeof SetJuloPublicationSchema>
export type MoveBikeForSale = z.infer<typeof MoveBikeForSaleSchema>
export type MarkBikeForSaleHandedOver = z.infer<
  typeof MarkBikeForSaleHandedOverSchema
>
export type SubmitBikeForSaleInspection = z.infer<
  typeof SubmitBikeForSaleInspectionSchema
>
export type SubmitBikeForSaleInspectionEvaluatedPart = z.infer<
  typeof SubmitBikeForSaleInspectionEvaluatedPartSchema
>
export type SubmitBikeForSaleInspectionManualAction = z.infer<
  typeof SubmitBikeForSaleInspectionManualActionSchema
>
export type BikeForSaleProposalPreviewInput = z.infer<
  typeof BikeForSaleProposalPreviewSchema
>

export interface BikeForSaleProposalLine {
  kind: "PACKAGE" | "WASH" | "FEE" | "MANUAL_ACTION" | "PART_LABOR"
  refId: string | null
  name: string
  priceKr: number
  laborMinutes: number
}

export interface BikeForSaleProposalDto {
  totalKr: number
  partsKr: number
  laborKr: number
  laborMinutes: number
  lines: BikeForSaleProposalLine[]
  receipt: Receipt
}

export interface BikeForSaleSummary {
  id: string
  displayNumber: number
  type: BikeForSaleType
  brand: string
  model: string | null
  color: string | null
  bikeType: ServiceBikeType
  isElectric: boolean
  acquisitionCost: number | null
  estimatedSalePrice: number | null
  status: BikeForSaleStatus
  brakeSystemFront: ServiceBikeBrakeSystem | null
  brakeSystemBack: ServiceBikeBrakeSystem | null
  gearSystem: ServiceBikeGearSystem | null
  bikeHasFrontGears: boolean
  hasElectricFrontGears: boolean
  hasElectricRearGears: boolean
  bikeHasDamperFront: boolean
  bikeHasDamperBack: boolean
  frameSizeCm: number | null
  frameSizeLetter: BikeFrameSize | null
  wheelSizeInches: number | null
  weightKg: number | null
  frameNumber: string | null
  articleNumber: string | null
  numberOfGears: number | null
  listingPrice: number | null
  storageLocationId: string | null
  posOrderId: string | null
  posOrderName: string | null
  publishedOnJulo: boolean
  soldVia: BikeSoldVia | null
  soldAt: string | null
  /** Confirmed buyer email from Stripe Checkout — JULO_SE sales only. */
  buyerEmail: string | null
  handedOverAt: string | null
  createdAt: string
  updatedAt: string
}

/** Bikes sold on julo.se that the buyer has not picked up yet — drives the
 * info badge on the CRM's "Cyklar" nav item. */
export interface BikeForSaleAwaitingHandoverCountDto {
  count: number
}

export interface BikeForSaleInspectionPartDto {
  id: string
  partId: string
  grade: number
  recommendedAction: ServiceAction
  laborPrice: number
  comment: string | null
  damperBrandId: string | null
  sortPriority: number
  products: Product[]
}

export interface BikeForSaleInspectionPackageDto {
  id: string
  packageId: string
  packagePriceSnap: number
  packageLaborTimeMinutesSnap: number
  packagePartsAcquisitionCostSnap: number
}

export interface BikeForSaleInspectionWashDto {
  id: string
  washId: string
  nameSnap: string
  priceSnap: number
}

export interface BikeForSaleInspectionFeeDto {
  id: string
  feeId: string
  nameSnap: string
  priceSnap: number
}

export interface BikeForSaleInspectionManualActionDto {
  id: string
  name: string
  laborPrice: number
  laborMinutes: number
}

export interface BikeForSaleInspectionDto {
  id: string
  acquisitionCostSnap: number
  estimatedSalePriceSnap: number
  servicePartsAcquisitionCost: number
  laborCost: number
  totalServiceCost: number
  totalLaborMinutes: number
  hourlyCostRateSnap: number
  requiredMarginPercentSnap: number
  marginPercent: number | null
  marginOk: boolean
  mechanicChose: BikeForSaleStatus
  mechanicId: string
  createdAt: string
  proposalTotalKr: number
  proposalLaborTotalKr: number
  proposalPartsTotalKr: number
  proposalLaborMinutes: number
  brakeSystemFrontSnap: ServiceBikeBrakeSystem | null
  brakeSystemBackSnap: ServiceBikeBrakeSystem | null
  gearSystemSnap: ServiceBikeGearSystem | null
  bikeHasFrontGearsSnap: boolean
  hasElectricFrontGearsSnap: boolean
  hasElectricRearGearsSnap: boolean
  bikeHasDamperFrontSnap: boolean
  bikeHasDamperBackSnap: boolean
  removedPartIds: string[]
  evaluatedParts: BikeForSaleInspectionPartDto[]
  selectedPackages: BikeForSaleInspectionPackageDto[]
  selectedWashes: BikeForSaleInspectionWashDto[]
  selectedFees: BikeForSaleInspectionFeeDto[]
  manualActions: BikeForSaleInspectionManualActionDto[]
}

export interface BikeForSaleInspectionProtocolProductDto {
  id: string
  title: string
  quantity: number
  articleNumber: string | null
  ean: string | null
}

export interface BikeForSaleInspectionProtocolPartDto {
  partId: string
  partName: string
  partImage: string
  actionLabel: string
  /** Null when the action mounts a part the bike does not have yet. */
  grade: PartGrade | null
  comment: string | null
  products: BikeForSaleInspectionProtocolProductDto[]
}

export interface BikeForSaleInspectionProtocolItemDto {
  id: string
  name: string
}

/** The saved inspection with names resolved, ready to show as-is. */
export interface BikeForSaleInspectionProtocolDto {
  mechanicName: string
  inspectedAt: string
  partsWithAction: BikeForSaleInspectionProtocolPartDto[]
  /** Every graded part, shaped like the service proposal's "Cykelskick". */
  bikeCondition: ProposalBikeCondition
  packages: BikeForSaleInspectionProtocolItemDto[]
  washes: BikeForSaleInspectionProtocolItemDto[]
  fees: BikeForSaleInspectionProtocolItemDto[]
  manualActions: BikeForSaleInspectionProtocolItemDto[]
}

export interface BikeForSalePublicationDto {
  id: string
  bikeForSaleId: string
  target: BikeForSalePublicationTarget
  externalProductId: string
  externalAdminUrl: string | null
  publishedAt: string
}

export interface BikeForSaleDetailDto extends BikeForSaleSummary {
  inspection: BikeForSaleInspectionDto | null
  linkToPosOrder: string | null
  storageLocation: StorageLocationDto | null
  images: BikeForSaleImageDto[]
  publications: BikeForSalePublicationDto[]
  /** Julo's purchase invoice for this bike — JULO_SE sales only. */
  selfBillingInvoice: SelfBillingInvoiceDto | null
}

export interface BikeForSaleInspectionPrerequisitesPackageDto {
  id: string
  name: string
  description: string | null
  priceKr: number
  laborTimeMinutes: number
  partsAcquisitionCostKr: number
  partIds: string[]
}

export interface BikeForSaleInspectionPrerequisitesPartDto {
  id: string
  name: string
  image: string
  category: string
  sortPriority: number
  availableActions: string[]
  durations: Record<string, number>
  foundOnFrontBrakeSystems: string[]
  foundOnRearBrakeSystems: string[]
  foundOnGearSystems: string[]
  gearSystemRequirement: string
  frontSuspensionRequirement: string
  requiresRearSuspension: boolean
  isOnlyRelevantOnElectricBikes: boolean
  electricFrontGearsRequirement: string
  electricRearGearsRequirement: string
  wheelSetupRequirement: string
}

export interface BikeForSaleInspectionPrerequisitesWashDto {
  id: string
  name: string
  description: string | null
  priceKr: number
}

export interface BikeForSaleInspectionPrerequisitesFeeDto {
  id: string
  name: string
  description: string | null
  priceKr: number
}

export interface BikeForSaleInspectionPrerequisitesDamperBrandDto {
  id: string
  name: string
}

// Store-configured actions applicable to this bike's type, prefilled as
// editable manual-action rows when a fresh inspection is started. The
// configured price is the action's full cost — no laborMinutes, which would
// be costed on top via the hourly rate.
export interface BikeForSaleInspectionPrerequisitesDefaultActionDto {
  name: string
  laborPrice: number
}

export interface BikeForSaleInspectionPrerequisitesDto {
  bike: BikeForSaleDetailDto
  hourlyCostRate: number
  requiredMarginPercent: number
  packages: BikeForSaleInspectionPrerequisitesPackageDto[]
  parts: BikeForSaleInspectionPrerequisitesPartDto[]
  washes: BikeForSaleInspectionPrerequisitesWashDto[]
  fees: BikeForSaleInspectionPrerequisitesFeeDto[]
  damperBrands: BikeForSaleInspectionPrerequisitesDamperBrandDto[]
  mechanics: { id: string; name: string }[]
  /** Logged-in user's linked Personal, else the first mechanic */
  defaultMechanicId: string | null
  defaultManualActions: BikeForSaleInspectionPrerequisitesDefaultActionDto[]
}
